import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { cuppingsPath, cuppingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

interface Sample {
  id: number
  name: string
}

interface Cupping {
  id: number
  title: string
  cupping_form: string
  cupping_form_label: string
  samples: Sample[]
}

interface SampleExtrinsic {
  id: number
  name: string
  extrinsic_data: ExtrinsicData | null
}

interface ExtrinsicData {
  farming: {
    checks: string[]
    notes: string
  }
  processing: {
    checks: string[]
    notes: string
  }
  trading: {
    checks: string[]
    notes: string
  }
  certifications: {
    checks: string[]
    notes: string
  }
  other: {
    checks: string[]
    notes: string
  }
}

interface Props {
  cupping: Cupping
  samples_extrinsic: SampleExtrinsic[]
}

const EMPTY_EXTRINSIC: ExtrinsicData = {
  farming: { checks: [], notes: "" },
  processing: { checks: [], notes: "" },
  trading: { checks: [], notes: "" },
  certifications: { checks: [], notes: "" },
  other: { checks: [], notes: "" },
}

const FARMING_CHECKS = [
  { key: "country", label: "Pais" },
  { key: "region", label: "Region" },
  { key: "farm_name", label: "Nombre de finca o cooperativa" },
  { key: "producer_name", label: "Nombre del productor(es)" },
  { key: "species", label: "Especie" },
  { key: "variety", label: "Variedad o mezcla de variedades" },
  { key: "harvest_date", label: "Fecha/ano de cosecha" },
  { key: "other", label: "Otro" },
]

const PROCESSING_CHECKS = [
  { key: "processor_name", label: "Nombre del procesador(es)", indent: false },
  { key: "wet_mill", label: "Beneficio humedo / Estacion", indent: true },
  { key: "dry_mill", label: "Beneficio seco", indent: true },
  { key: "processor_other", label: "Otro", indent: true },
  { key: "process_type", label: "Tipo de proceso", indent: false },
  { key: "washed", label: "Lavado", indent: true },
  { key: "natural", label: "Natural", indent: true },
  { key: "process_other", label: "Otro", indent: true },
  { key: "decaffeinated", label: "Descafeinado", indent: false },
  {
    key: "process_description",
    label: "Descripcion del proceso",
    indent: false,
  },
]

const TRADING_CHECKS = [
  { key: "grade", label: "Grado" },
  { key: "ico_number", label: "Numero ICO" },
  { key: "importer_name", label: "Nombre del importador" },
  { key: "exporter_name", label: "Nombre del exportador" },
  { key: "farm_gate_price", label: "Precio en finca" },
  { key: "lot_size", label: "Tamano de lote" },
  { key: "other", label: "Otro" },
]

const CERTIFICATION_CHECKS = [
  { key: "4c", label: "4C" },
  { key: "fair_trade", label: "Comercio justo (Fair trade)" },
  { key: "organic", label: "Organico" },
  { key: "rainforest_alliance", label: "Rainforest Alliance" },
  { key: "food_safety", label: "Seguridad alimentaria" },
  { key: "other", label: "Otro" },
]

const OTHER_CHECKS = [{ key: "awards", label: "Premios" }]

function CheckboxSection({
  title,
  items,
  checks,
  onToggle,
  notes,
  onNotesChange,
  notesLabel,
}: {
  title: string
  items: { key: string; label: string; indent?: boolean }[]
  checks: string[]
  onToggle: (key: string) => void
  notes: string
  onNotesChange: (v: string) => void
  notesLabel: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-sm font-bold tracking-wide uppercase">
              {title}
            </h3>
            <div className="space-y-2">
              {items.map((item) => (
                <label
                  key={item.key}
                  className={`flex cursor-pointer items-center gap-2 ${item.indent ? "ml-5" : ""}`}
                >
                  <Checkbox
                    checked={checks.includes(item.key)}
                    onCheckedChange={() => onToggle(item.key)}
                  />
                  <span className="text-sm">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">{notesLabel}</Label>
            <Textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Informacion relevante..."
              rows={6}
              className="resize-none text-sm"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function ExtrinsicPage({ cupping, samples_extrinsic }: Props) {
  const [currentSample, setCurrentSample] = useState(0)
  const currentExtrinsic =
    samples_extrinsic[currentSample]?.extrinsic_data || EMPTY_EXTRINSIC

  const { data, setData, post, processing } = useForm({
    sample_id: samples_extrinsic[0]?.id,
    extrinsic_data: currentExtrinsic,
  })

  const handleSampleChange = (index: number) => {
    setCurrentSample(index)
    const ext = samples_extrinsic[index]?.extrinsic_data || EMPTY_EXTRINSIC
    setData({
      sample_id: samples_extrinsic[index]?.id,
      extrinsic_data: ext,
    })
  }

  const toggleCheck = (category: keyof ExtrinsicData, key: string) => {
    const current = data.extrinsic_data[category].checks
    const updated = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key]
    setData("extrinsic_data", {
      ...data.extrinsic_data,
      [category]: { ...data.extrinsic_data[category], checks: updated },
    })
  }

  const setNotes = (category: keyof ExtrinsicData, value: string) => {
    setData("extrinsic_data", {
      ...data.extrinsic_data,
      [category]: { ...data.extrinsic_data[category], notes: value },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/cuppings/${cupping.id}/extrinsic`)
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Sesiones de catacion", href: cuppingsPath() },
    { title: cupping.title, href: cuppingPath(cupping.id) },
    { title: "Evaluacion extrinseca", href: "#" },
  ]

  const sampleName =
    samples_extrinsic[currentSample]?.name || `Muestra #${currentSample + 1}`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Evaluacion Extrinseca - ${cupping.title}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <Link
            href={cuppingPath(cupping.id)}
            className="text-muted-foreground hover:text-foreground mb-2 flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a sesion
          </Link>
          <h1 className="text-3xl font-bold">Evaluacion Extrinseca</h1>
          <p className="text-muted-foreground text-sm">
            SCA Standard 105-2025 - Informacion extrinseca de la muestra
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sample Selector */}
          <div className="flex items-center gap-4 border-b pb-4">
            <div className="flex items-center gap-2">
              {samples_extrinsic.map((s, idx) => (
                <Button
                  key={s.id}
                  type="button"
                  variant={currentSample === idx ? "default" : "outline"}
                  onClick={() => handleSampleChange(idx)}
                  className={currentSample === idx ? "bg-primary" : ""}
                >
                  {idx + 1}
                </Button>
              ))}
            </div>
            <div className="flex-1" />
            <p className="text-muted-foreground text-sm">{sampleName}</p>
          </div>

          {/* Farming & Processing */}
          <div className="grid gap-4 md:grid-cols-2">
            <CheckboxSection
              title="Agricultura"
              items={FARMING_CHECKS}
              checks={data.extrinsic_data.farming.checks}
              onToggle={(key) => toggleCheck("farming", key)}
              notes={data.extrinsic_data.farming.notes}
              onNotesChange={(v) => setNotes("farming", v)}
              notesLabel="Informacion agricola relevante"
            />
            <CheckboxSection
              title="Procesamiento"
              items={PROCESSING_CHECKS}
              checks={data.extrinsic_data.processing.checks}
              onToggle={(key) => toggleCheck("processing", key)}
              notes={data.extrinsic_data.processing.notes}
              onNotesChange={(v) => setNotes("processing", v)}
              notesLabel="Informacion de procesamiento relevante"
            />
          </div>

          {/* Trading & Certifications */}
          <div className="grid gap-4 md:grid-cols-2">
            <CheckboxSection
              title="Comercializacion"
              items={TRADING_CHECKS}
              checks={data.extrinsic_data.trading.checks}
              onToggle={(key) => toggleCheck("trading", key)}
              notes={data.extrinsic_data.trading.notes}
              onNotesChange={(v) => setNotes("trading", v)}
              notesLabel="Informacion comercial relevante"
            />
            <CheckboxSection
              title="Certificaciones"
              items={CERTIFICATION_CHECKS}
              checks={data.extrinsic_data.certifications.checks}
              onToggle={(key) => toggleCheck("certifications", key)}
              notes={data.extrinsic_data.certifications.notes}
              onNotesChange={(v) => setNotes("certifications", v)}
              notesLabel="Informacion de certificaciones relevante"
            />
          </div>

          {/* Other */}
          <CheckboxSection
            title="Otros"
            items={OTHER_CHECKS}
            checks={data.extrinsic_data.other.checks}
            onToggle={(key) => toggleCheck("other", key)}
            notes={data.extrinsic_data.other.notes}
            onNotesChange={(v) => setNotes("other", v)}
            notesLabel="Otra informacion relevante"
          />

          {/* Submit */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={processing}
              size="lg"
              className="bg-primary hover:bg-primary/90"
            >
              {processing ? "Guardando..." : "Guardar evaluacion extrinseca"}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
